import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export type ProgressStepState = 'complete' | 'current' | 'pending' | 'error';

export interface ProgressStep {
  /** Already translated - callers resolve it, so this component stays i18n-agnostic. */
  label: string;
  state: ProgressStepState;
}

/**
 * A read-only progress tracker: where a multi-stage process is, not a way to move through it.
 *
 * Not `mat-stepper`. That is an interactive wizard - it renders a `tablist` with one hidden,
 * animated tab panel per step, and always draws the selected step as a number, so a finished
 * process never looks finished. Using it as a tracker took a `::ng-deep pointer-events: none
 * !important` to stop the clicks and announced four tabs to screen readers.
 *
 * Each step carries its own state, so the caller decides what "done" means - e.g. a failed run
 * marks its last step `error` rather than the tracker guessing from an index.
 */
@Component({
  selector: 'app-progress-steps',
  templateUrl: './progress-steps.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressStepsComponent {
  @Input() steps: ProgressStep[] = [];

  /** Spins the current step's marker, for work happening out of the user's hands (a queued job). */
  @Input() busy = false;

  readonly markerClasses: Record<ProgressStepState, string> = {
    complete: 'bg-theme-600 text-white',
    current: 'border-2 border-solid border-theme-600 bg-white text-theme-600',
    pending: 'border-2 border-solid border-gray-300 bg-white text-gray-500',
    error: 'bg-status-red text-white',
  };

  readonly labelClasses: Record<ProgressStepState, string> = {
    complete: 'text-theme-text',
    current: 'font-medium text-theme-text',
    pending: 'text-gray-500',
    error: 'font-medium text-status-red',
  };
}
